'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import { useState, useEffect } from 'react';
import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import type { SectionProps } from './custom-homepage';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import {
  CarouselProvider,
  useCarouselContext,
} from '@eliadmin/core/src/components/ui/carousel-context';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { calculateReadingTime } from '@eliadmin/core/src/utils/calculate-reading-time';
import Image from 'next/image';

const DURATION = 5000;
const RADIUS = 17;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

type BlogsProps = { items: Blog[] } & Partial<SectionProps>;

export function Blogs(_props: BlogsProps) {
  return (
    <CarouselProvider>
      <BlogsInner _props={_props} />
    </CarouselProvider>
  );
}

function BlogsInner({ _props }: { _props: BlogsProps }) {
  const { t } = useTranslations();
  const { selectedIndex, api } = useCarouselContext()!;

  useEffect(() => {
    if (!api) return;
    const timer = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, DURATION);
    return () => clearInterval(timer);
  }, [api]);

  return (
    <section
      className={cn(
        'custom-section',
        'generic-gap',
        'pb-16 pt-8 sm:pb-20 sm:pt-10 lg:pb-24 lg:pt-12',
      )}
    >
      <div className={cn('custom-container', 'flex items-end justify-between gap-6')}>
        {_props?.title && <Heading subtitle={_props.subtitle} title={_props.title} />}
        <Button asChild variant={'secondary'} className="hidden whitespace-nowrap sm:flex">
          <Link prefetch={false} href={ROUTES.BLOG()}>
            {t('text2000')}
          </Link>
        </Button>
      </div>
      <div className={cn('custom-container', 'relative overflow-hidden rounded-[20px]')}>
        <Carousel
          className={{
            slide: 'w-full',
            wrapper: 'w-full',
            container: 'w-full',
            main: 'w-full',
          }}
          slides={_props.items.map(blog => (
            <BlogCard blog={blog} key={blog.slug} />
          ))}
        />
        <div className="absolute bottom-4 right-4 z-10 flex items-center justify-end gap-2 sm:bottom-6 sm:right-6 sm:gap-3 lg:bottom-8 lg:right-8 lg:gap-4">
          {_props.items.map((_, index) => (
            <CircleProgress key={index} index={index} selectedIndex={selectedIndex} />
          ))}
        </div>
      </div>
      <Button asChild variant={'secondary'} className="flex w-full sm:hidden">
        <Link prefetch={false} href={ROUTES.BLOG()}>
          {t('text2000')}
        </Link>
      </Button>
    </section>
  );
}

function CircleProgress({ index, selectedIndex }: { index: number; selectedIndex: number }) {
  const isCompleted = index < selectedIndex;
  const isActive = index === selectedIndex;
  const [offset, setOffset] = useState(CIRCUMFERENCE);

  useEffect(() => {
    if (isCompleted) {
      setOffset(0);
    } else if (isActive) {
      setOffset(CIRCUMFERENCE);
      const timer = setTimeout(() => setOffset(0), 10);
      return () => clearTimeout(timer);
    } else {
      setOffset(CIRCUMFERENCE);
    }
  }, [isActive, isCompleted]);

  return (
    <div className="relative flex h-8 w-8 items-center justify-center sm:h-9 sm:w-9 lg:h-10 lg:w-10">
      <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r={RADIUS} stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
        <circle
          cx="20"
          cy="20"
          r={RADIUS}
          stroke="white"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          style={{
            transition: isActive
              ? `stroke-dashoffset ${DURATION}ms linear`
              : 'stroke-dashoffset 300ms ease-in-out',
          }}
        />
      </svg>
      <span className="relative z-10 text-center text-xs font-medium text-white sm:text-sm">
        {index + 1}
      </span>
    </div>
  );
}

function BlogCard({ blog }: { blog: Blog }) {
  const { t } = useTranslations();
  const textContent = getTextFromHTML(blog.content);
  const locale = useLocaleStore(state => state.currentLocale);

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  return (
    <div className="relative flex h-[500px] w-full items-end justify-end overflow-hidden rounded-[20px] px-4 pb-[72px] sm:px-6 sm:pb-6 lg:px-8 lg:pb-8">
      <div className="relative z-10 flex w-full flex-col items-start justify-start gap-6 sm:gap-8">
        <div className="flex w-full flex-col items-start justify-start gap-2 sm:gap-4">
          <div className="flex items-center justify-start gap-2">
            <Paragraph size="base" color="white">
              {formattedDate}
            </Paragraph>
            <div className="h-[18px] w-[1px] bg-[#666666]" />
            <Paragraph size="base" color="white">
              {calculateReadingTime(textContent)} {t('text3360')}
            </Paragraph>
          </div>
          <Heading title={blog.name} className={{ title: 'text-white' }} tag="h3" tracking={true} />
        </div>
        <Button asChild variant={'secondary'} className="h-[50px] sm:h-[52px] lg:h-14">
          <Link href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}>{t('text1148')}</Link>
        </Button>
      </div>
      <div className="main-overlay" />
      <div className="main-blog-overlay" />
      <Image
        src={blog?.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(blog?.image, blog?.name)}
        fill
        className="absolute inset-0 z-0 h-full w-full object-cover object-center grayscale"
      />
    </div>
  );
}
